/**
 * Page layout settings : single source of truth for the admin.
 *
 * The shape mirrors `PageLayoutSettings` from the public renderer SDK
 * (`@bcc/core-renderers`); the runtime contract lives there. We duplicate the
 * type here on purpose so the admin doesn't take a hard dependency on the SDK
 * package : the admin only needs to round-trip these fields verbatim through
 * the API. If a new field is added on the SDK side, mirror it here.
 *
 * This module also owns the labels, descriptions, and defaults used by the
 * admin form panel. Centralizing these (rather than hardcoding in JSX) keeps
 * the form file small and lets the same metadata feed tooltips, validation,
 * and any future bulk-edit / content-type-default UI.
 */

export type PageLayoutMode = "fluid" | "constrained";

export type PageLayoutSurface = "white" | "tint";

export interface PageLayoutSettings {
	/**
	 * "constrained" (default) → page <main> uses a max-width and side padding.
	 * "fluid"                  → no max-width, no padding; components render
	 *                              edge-to-edge (used by landing pages).
	 */
	mode?: PageLayoutMode;
	/**
	 * "white" (default) → normal white page background.
	 * "tint"            → pale blue page background (#F4F8FC) on which the white
	 *                      header band and white panels read as cards. This is
	 *                      the statistics subject-page look; pair it with
	 *                      "fluid" so each block carries its own gutter.
	 */
	surface?: PageLayoutSurface;
	/**
	 * On mobile only, the FIRST widget spans the viewport (escapes the main's
	 * side padding) and sits flush against the top of the page area. Desktop
	 * is unaffected. Use for pages whose first component is a hero.
	 */
	firstWidgetMobileFlush?: boolean;
	/**
	 * The LAST widget gets a flush bottom : no bottom padding inside <main>.
	 * Use for pages ending in a full-bleed component (e.g., NewsletterSignup).
	 */
	lastWidgetFlushBottom?: boolean;
}

/** Effective defaults applied at runtime by the SDK when a field is unset. */
export const PAGE_LAYOUT_DEFAULTS: Required<PageLayoutSettings> = {
	mode: "constrained",
	surface: "white",
	firstWidgetMobileFlush: false,
	lastWidgetFlushBottom: false,
};

/** Display options for the surface select. Empty means "leave unset". */
export const PAGE_LAYOUT_SURFACE_OPTIONS: ReadonlyArray<{
	value: "" | PageLayoutSurface;
	label: string;
	description: string;
}> = [
	{
		value: "",
		label: "Défaut (blanc)",
		description: "Fond de page blanc. Convient à la quasi-totalité des pages.",
	},
	{
		value: "white",
		label: "Blanc",
		description: "Fond blanc explicite.",
	},
	{
		value: "tint",
		label: "Bleu très clair",
		description:
			"Fond bleu pâle (#F4F8FC) : le bandeau de titre et les panneaux blancs ressortent comme des cartes. Gabarit des pages-sujets statistiques ; à combiner avec la largeur « Fluide ».",
	},
];

/** Display options for the mode select. The empty value means "leave unset
 *  on the payload", which lets the runtime default apply. We surface this as
 *  a real option so authors can clear an override. */
export const PAGE_LAYOUT_MODE_OPTIONS: ReadonlyArray<{
	value: "" | PageLayoutMode;
	label: string;
	description: string;
}> = [
	{
		value: "",
		label: "Default (constrained)",
		description: "Use the platform default. Recommended for most pages.",
	},
	{
		value: "constrained",
		label: "Constrained",
		description: "Centered content with max width and side padding (article pages, listings).",
	},
	{
		value: "fluid",
		label: "Fluid (full-bleed)",
		description: "No max width, no padding. Components render edge-to-edge (landing pages).",
	},
];

/** Field-level metadata for boolean toggles, used by the form to render captions
 *  consistently and by future bulk-edit UIs. */
export interface PageLayoutFlagDescriptor {
	key: keyof Pick<PageLayoutSettings, "firstWidgetMobileFlush" | "lastWidgetFlushBottom">;
	label: string;
	description: string;
}

export const PAGE_LAYOUT_FLAGS: ReadonlyArray<PageLayoutFlagDescriptor> = [
	{
		key: "firstWidgetMobileFlush",
		label: "First widget full-bleed on mobile",
		description:
			"On mobile, the first widget (hero) spans the viewport edge-to-edge and hugs the header bar. Desktop is unaffected.",
	},
	{
		key: "lastWidgetFlushBottom",
		label: "Last widget flush to page bottom",
		description:
			"Removes the bottom padding so the last widget (e.g., a newsletter signup) sits at the page edge.",
	},
];

/** Returns true when the layout object would have no effect (all defaults).
 *  Use this when serializing : empty layouts can be dropped from the payload. */
export function isLayoutEmpty(layout: PageLayoutSettings | null | undefined): boolean {
	if (!layout) return true;
	const hasMode = layout.mode === "fluid" || layout.mode === "constrained";
	const hasSurface = layout.surface === "white" || layout.surface === "tint";
	const hasFirst = layout.firstWidgetMobileFlush === true;
	const hasLast = layout.lastWidgetFlushBottom === true;
	return !hasMode && !hasSurface && !hasFirst && !hasLast;
}

/** Builds a normalized layout object for persistence. Drops false flags and
 *  unset modes so MinIO payloads stay small and diff-friendly. Returns null
 *  when the resulting object would be empty. */
export function normalizeLayoutForSave(
	layout: PageLayoutSettings | null | undefined
): PageLayoutSettings | null {
	if (!layout) return null;
	const next: PageLayoutSettings = {};
	if (layout.mode === "fluid" || layout.mode === "constrained") {
		next.mode = layout.mode;
	}
	if (layout.surface === "white" || layout.surface === "tint") {
		next.surface = layout.surface;
	}
	if (layout.firstWidgetMobileFlush === true) {
		next.firstWidgetMobileFlush = true;
	}
	if (layout.lastWidgetFlushBottom === true) {
		next.lastWidgetFlushBottom = true;
	}
	return Object.keys(next).length > 0 ? next : null;
}
